import { useMemo, useState, type FormEvent } from 'react';
import { MIN_PASSWORD_LENGTH } from '../lib/password';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../lib/api';
import { Alert, Button, Field, PasswordInput, Select, TextInput } from '../components/ui';
import { usePortal } from '../lib/portal';
import '../styles/auth.css';

import { EnvBadge } from '../components/EnvBadge';
// Country → default currency. East Africa first, since that is the market.
const COUNTRIES: { code: string; name: string; currency: string }[] = [
  { code: 'KE', name: 'Kenya', currency: 'KES' },
  { code: 'UG', name: 'Uganda', currency: 'UGX' },
  { code: 'TZ', name: 'Tanzania', currency: 'TZS' },
  { code: 'RW', name: 'Rwanda', currency: 'RWF' },
];
const CURRENCIES = ['KES', 'UGX', 'TZS', 'RWF', 'USD'];

function strength(pw: string): { pct: number; color: string; label: string } {
  let score = 0;
  if (pw.length >= 10) score += 2; else if (pw.length >= 6) score += 1;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  const pct = Math.min(100, (score / 5) * 100);
  if (score <= 1) return { pct, color: 'var(--danger-600)', label: 'Weak' };
  if (score <= 3) return { pct, color: 'var(--warning-600)', label: 'Fair' };
  return { pct, color: 'var(--success-600)', label: 'Strong' };
}

export function Register() {
  const { siteHome } = usePortal();
  const { register } = useAuth();
  const navigate = useNavigate();

  const [f, setF] = useState({
    fullName: '', phone: '', email: '', password: '',
    countryCode: 'KE', currencyCode: 'KES', accountType: 'individual_landlord',
  });
  const [terms, setTerms] = useState(false);
  const [error, setError] = useState<{ tone: 'danger' | 'info'; node: React.ReactNode } | null>(null);
  const [busy, setBusy] = useState(false);

  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((s) => ({ ...s, [k]: e.target.value }));

  const onCountry = (e: { target: { value: string } }) => {
    const c = COUNTRIES.find((x) => x.code === e.target.value);
    setF((s) => ({ ...s, countryCode: e.target.value, currencyCode: c?.currency ?? s.currencyCode }));
  };

  const pw = useMemo(() => strength(f.password), [f.password]);
  const canSubmit = f.fullName.trim().length >= 2 && f.email.includes('@') &&
    f.phone.trim().length >= 7 && f.password.length >= 10 && terms;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await register({ ...f, acceptedTerms: true });
      navigate('/', { replace: true });
    } catch (err) {
      // Values are preserved: state is untouched on failure (7.2). An existing
      // email offers the next step rather than a bare rejection.
      if (err instanceof ApiError && (err.status === 409 || /already registered/i.test(err.message))) {
        setError({
          tone: 'info',
          node: (
            <>That email already has an account. <Link to="/login">Sign in</Link> or{' '}
              <Link to="/forgot-password">reset your password</Link>.</>
          ),
        });
      } else if (err instanceof ApiError) {
        setError({ tone: 'danger', node: err.message });
      } else {
        setError({ tone: 'danger', node: 'We could not create your account. Please try again.' });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-wrap">
      <div className="auth-brand-row">
        <Link to={siteHome} className="auth-brand">NyumbaSuite</Link>
        <EnvBadge />
      </div>
      <form className="auth-card register" onSubmit={submit} noValidate>
        <h1 className="auth-title">Create your account</h1>
        <p className="auth-lede">Next, you’ll add your first property and units. It takes a few minutes.</p>

        {error && <div style={{ marginBottom: 16 }}><Alert tone={error.tone}>{error.node}</Alert></div>}

        <Field label="Full name" htmlFor="fullName">
          <TextInput id="fullName" value={f.fullName} autoComplete="name" autoFocus
            onChange={set('fullName')} required />
        </Field>

        <Field label="Phone" htmlFor="phone" hint="We use this for rent and account notifications.">
          <TextInput id="phone" value={f.phone} type="tel" inputMode="tel" autoComplete="tel"
            placeholder="+254…" onChange={set('phone')} required />
        </Field>

        <Field label="Email" htmlFor="email">
          <TextInput id="email" value={f.email} type="email" inputMode="email" autoComplete="email"
            onChange={set('email')} required />
        </Field>

        <Field label="Password" htmlFor="password" hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}>
          <PasswordInput id="password" value={f.password} autoComplete="new-password"
            onChange={set('password')} required />
          {f.password.length > 0 && (
            <>
              <div className="strength"><span style={{ width: `${pw.pct}%`, background: pw.color }} /></div>
              <span className="field-hint" style={{ color: pw.color }}>{pw.label}</span>
            </>
          )}
        </Field>

        <div className="grid grid-2">
          <Field label="Country" htmlFor="country">
            <Select id="country" value={f.countryCode} onChange={onCountry}>
              {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Currency" htmlFor="currency">
            <Select id="currency" value={f.currencyCode} onChange={set('currencyCode')}>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </Field>
        </div>

        <Field label="Account type" htmlFor="accountType">
          <Select id="accountType" value={f.accountType} onChange={set('accountType')}>
            <option value="individual_landlord">Individual landlord</option>
            <option value="property_manager">Property manager</option>
            <option value="company">Company</option>
          </Select>
        </Field>

        <label className="check" style={{ marginBottom: 20 }}>
          <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} />
          <span>I agree to the <a href="/terms" target="_blank" rel="noreferrer">Terms</a> and{' '}
            <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.</span>
        </label>

        <div className="auth-pin">
          <Button type="submit" block loading={busy} disabled={!canSubmit}>Create account</Button>
        </div>

        <div className="auth-foot">Already have an account? <Link to="/login">Sign in instead</Link></div>
      </form>
      <Link className="auth-link" to={siteHome}>← Back to homepage</Link>
    </div>
  );
}
